// src/pages/VideoAnalysePage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import axios from 'axios';
import { videoHeaders, isVerificatieFout, meldVerificatieVerlopen } from '../utils/videoVerificatie';
import VideoPrivacyInfo from '../components/VideoPrivacyInfo';
import { nietAangekomenTekst } from '../utils/videoUpload';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const POLL_INTERVAL_MS = 5000;

// Een poging zonder teken van leven (de server zegt dat met niet_aangekomen) is niet bezig.
function isActief(a) {
  return (a.status === 'uploading' && !a.niet_aangekomen) || a.status === 'processing';
}

function getStatusBadge(analyse) {
  if (analyse.niet_aangekomen) return { label: 'Niet aangekomen', className: 'badge-error' };
  switch (analyse.status) {
    case 'done':
      return { label: 'Verwerkt', className: 'badge-done' };
    case 'processing':
      return { label: 'Bezig...', className: 'badge-processing' };
    case 'error':
      return { label: 'Fout', className: 'badge-error' };
    case 'uploading':
      return { label: 'Uploaden...', className: 'badge-uploading' };
    default:
      return { label: 'Onbekend', className: '' };
  }
}

function getStatusDescription(status) {
  switch (status) {
    case 'uploading':
      return 'Je video wordt geüpload. Daarna begint de analyse vanzelf.';
    case 'processing':
      return 'De AI analyseert je video aan de hand van het doorloopje. Dit kan 2-10 minuten duren.';
    default:
      return '';
  }
}

function getCardLink(analyse) {
  if (analyse.status === 'done') {
    return `/video-analyse/${analyse.documentId}/rapport`;
  }
  return `/video-analyse/${analyse.documentId}`;
}

function formatRelativeDate(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Zojuist';
  if (diffMin < 60) return `${diffMin} min geleden`;
  if (diffHrs < 24) return diffHrs === 1 ? '1 uur geleden' : `${diffHrs} uur geleden`;
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Of een analyse vastzit, zegt de server (vastgelopen): die telt vanaf het begin van de verwerking.
function ElapsedTimer({ since, vastgelopen }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    function update() {
      const diffMs = Date.now() - new Date(since).getTime();
      const totalSec = Math.max(0, Math.floor(diffMs / 1000));
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      if (min === 0) setElapsed(`${sec}s`);
      else setElapsed(`${min}m ${sec < 10 ? '0' : ''}${sec}s`);
    }
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [since]);

  if (vastgelopen) {
    return <span className="elapsed-timer elapsed-timer-stale">Analyse lijkt vast te staan ({elapsed})</span>;
  }
  return <span className="elapsed-timer">Bezig sinds {elapsed}</span>;
}

function getPreviewText(analyse) {
  const result = analyse.result;
  if (!result?.stappen?.length) return null;
  const firstObs = result.stappen[0].observatie;
  if (!firstObs) return null;
  return firstObs.length > 120 ? firstObs.slice(0, 117) + '...' : firstObs;
}

function VideoAnalysePage() {
  const { currentUser } = useDoorloopjes();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchAnalyses = useCallback(async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.get(
        `${STRAPI_URL}/api/video-analyses?populate[doorloopje][fields][0]=documentId&populate[doorloopje][fields][1]=title&fields[0]=documentId&fields[1]=status&fields[2]=createdAt&fields[3]=result&fields[4]=original_filename&sort[0]=createdAt:desc`,
        { headers: videoHeaders(jwt) }
      );
      setAnalyses(res.data.data);
      return res.data.data;
    } catch (err) {
      if (isVerificatieFout(err)) {
        meldVerificatieVerlopen();
        return [];
      }
      console.error('Failed to fetch analyses:', err);
      setError('Je analyses laden lukte niet. Probeer het opnieuw.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + polling for active analyses
  useEffect(() => {
    fetchAnalyses();
    pollRef.current = setInterval(async () => {
      const data = await fetchAnalyses();
      const hasActive = data.some(isActief);
      if (!hasActive && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [fetchAnalyses]);

  // Resume polling on tab re-focus if there are active analyses
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const hasActive = analyses.some(isActief);
        if (hasActive && !pollRef.current) {
          fetchAnalyses();
          pollRef.current = setInterval(fetchAnalyses, POLL_INTERVAL_MS);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchAnalyses, analyses]);

  async function handleDelete(documentId) {
    if (!window.confirm('Wil je deze analyse verwijderen? We verwijderen dan je video en je rapport. Dat kun je niet terugdraaien.')) return;
    try {
      const jwt = localStorage.getItem('jwt');
      await axios.delete(
        `${STRAPI_URL}/api/video-analyses/${documentId}`,
        { headers: videoHeaders(jwt) }
      );
      setAnalyses((prev) => prev.filter((a) => a.documentId !== documentId));
    } catch (err) {
      if (isVerificatieFout(err)) return meldVerificatieVerlopen();
      console.error('Delete error:', err);
      alert('Verwijderen lukte niet. Probeer het opnieuw.');
    }
  }

  if (!currentUser?.has_video_analysis) {
    return <Navigate to="/dashboard" replace />;
  }

  // Split into active and completed
  const activeAnalyses = analyses.filter(a => isActief(a) || a.niet_aangekomen);
  const completedAnalyses = analyses.filter(a => !isActief(a) && !a.niet_aangekomen);

  const introductie = (
    <div className="page-introduction">
      <div className="page-header-row">
        <h1>Video Analyse</h1>
        <VideoPrivacyInfo />
      </div>
      <p>Upload een video van je les en krijg feedback van de AI. Een doorloopje is de lens. Je krijgt per stap wat de AI zag, sterke punten en verbeterpunten.</p>
      <p>Hoe film je je les? <Link to="/doorloopjes/film-je-eigen-les">{"Lees 'Film je eigen les'"}</Link></p>
    </div>
  );

  if (loading) {
    return (
      <div className="page-container video-analyse-page">
        {introductie}
        <main className="video-analyse-content">
          <p>Laden...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="page-container video-analyse-page">
      {introductie}
      <main className="video-analyse-content">
        <Link to="/video-analyse/nieuw" className="btn-primary-action" style={{ marginBottom: '2rem' }}>
          + Nieuwe analyse starten
        </Link>

        {error && <p className="field-error">{error}</p>}

        {/* Active analyses — prominent cards */}
        {activeAnalyses.length > 0 && (
          <div className="active-analyses">
            {activeAnalyses.map((analyse) => {
              const badge = getStatusBadge(analyse);
              return (
                <div className={`active-analyse-card${analyse.vastgelopen || analyse.niet_aangekomen ? ' active-analyse-stale' : ''}`} key={analyse.documentId}>
                  <div className="active-analyse-header">
                    <span className={`analyse-badge ${badge.className}`}>
                      <span className="badge-dot"></span>
                      {badge.label}
                    </span>
                    <span className="analyse-date">{formatRelativeDate(analyse.gestart_op)}</span>
                  </div>
                  <h3 className="active-analyse-title">
                    {analyse.doorloopje?.title || 'Onbekend doorloopje'}
                  </h3>
                  {analyse.original_filename && (
                    <p className="active-analyse-detail">{analyse.original_filename}</p>
                  )}
                  {analyse.niet_aangekomen ? (
                    <>
                      <p className="active-analyse-description stale-description">
                        {nietAangekomenTekst(analyse)}
                      </p>
                      <Link className="btn-primary-action" to={`/video-analyse/nieuw?hervat=${analyse.documentId}`}>
                        Opnieuw proberen
                      </Link>
                      <button
                        className="btn-cancel-analyse"
                        onClick={() => handleDelete(analyse.documentId)}
                      >
                        Verwijderen
                      </button>
                    </>
                  ) : analyse.vastgelopen ? (
                    <>
                      <p className="active-analyse-description stale-description">
                        Deze analyse reageert niet meer. Dit kan voorkomen bij een serverprobleem of netwerkverstoring.
                      </p>
                      <ElapsedTimer since={analyse.bezig_sinds} vastgelopen />
                      <button
                        className="btn-cancel-analyse"
                        onClick={() => handleDelete(analyse.documentId)}
                      >
                        Analyse annuleren
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="active-analyse-description">{getStatusDescription(analyse.status)}</p>
                      <ElapsedTimer since={analyse.bezig_sinds} />
                      <div className="polling-indicator">
                        <span className="polling-dot"></span>
                        <span>Status wordt automatisch bijgewerkt...</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Completed / error analyses */}
        {!error && analyses.length === 0 && (
          <div className="empty-state">
            <svg className="empty-state-icon" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="12" width="48" height="36" rx="4" stroke="#d0d2d3" strokeWidth="2.5" fill="none"/>
              <polygon points="26,24 42,32 26,40" fill="#d0d2d3"/>
              <line x1="20" y1="56" x2="44" y2="56" stroke="#d0d2d3" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="32" y1="48" x2="32" y2="56" stroke="#d0d2d3" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <p>Je hebt nog geen video-analyses.</p>
            <p className="empty-state-sub">Upload een video van je les en kies een doorloopje. Binnen een paar minuten krijg je feedback.</p>
            <Link to="/video-analyse/nieuw" className="btn-primary-action">
              Eerste analyse starten
            </Link>
          </div>
        )}

        {completedAnalyses.length > 0 && (
          <>
            {activeAnalyses.length > 0 && (
              <h2 className="analyse-section-heading">Eerdere analyses</h2>
            )}
            <div className="analyse-list">
              {completedAnalyses.map((analyse) => {
                const badge = getStatusBadge(analyse);
                const preview = getPreviewText(analyse);
                return (
                  <div className="analyse-card" key={analyse.documentId}>
                    <Link to={getCardLink(analyse)} className="analyse-card-link">
                      <div className="analyse-card-header">
                        <span className={`analyse-badge ${badge.className}`}>
                          <span className="badge-dot"></span>
                          {badge.label}
                        </span>
                        <span className="analyse-date">{formatRelativeDate(analyse.gestart_op)}</span>
                      </div>
                      <h3 className="analyse-card-title">
                        {analyse.doorloopje?.title || 'Onbekend doorloopje'}
                      </h3>
                      {preview && <p className="analyse-card-preview">{preview}</p>}
                    </Link>
                    <button
                      className="analyse-delete-btn"
                      onClick={() => handleDelete(analyse.documentId)}
                      title="Verwijderen"
                      aria-label="Analyse verwijderen"
                    >
                      &#x2715;
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default VideoAnalysePage;
